import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import {
  CasesCredentialVerificationV1ApplicationContainerStaffUserCurrentDocument,
  CredentialVerificationCaseV1Application,
  CredentialVerificationCaseV1ApplicationReview,
  CredentialVerificationCaseV1UpdateInput,
  Entity,
  StaffCasesApplicationContainerCredentialVerificationCaseV1Document,
  StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateDocument,
  StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerEntitiesDocument
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { Country } from '../../../../../../types/country';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../../../shared/utils/queryOptions';
import { Application, ApplicationFormValueProps } from './application';
import { lookupCountryQuery } from './credential-verification-v1-country-query';

interface ApplicationContainerProps {
  caseId: string;
  isCaseOrApplicant: string;
}

export const ApplicationContainer: React.FC<ApplicationContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  const { data: entities, loading: entitiesLoading, error: entitiesError } = useQuery(StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerEntitiesDocument);

  const { data, loading, error } = useQuery(StaffCasesApplicationContainerCredentialVerificationCaseV1Document, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    }
  });

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError
  } = useQuery(CasesCredentialVerificationV1ApplicationContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions); 

  const [updateCredentialVerificationCase, { loading: isSavingApplication }] = useMutation(
    StaffCasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateDocument
  );

  const onSave = async (input: ApplicationFormValueProps) => {
    let inputData: CredentialVerificationCaseV1UpdateInput = {
      caseName: input.caseName,
      id: props.caseId,
      caseDetails: {
        application: {
          isCredentialInEnglish: input.isCredentialInEnglish,
          credentialType: input.credentialType,
          credentialTitle: input.credentialTitle,
          program: input.program,
          nameOnCredential: input.nameOnCredential,
          dateCredentialIssuedAt: input.dateCredentialIssuedAt,
          issuingInstitution: input.issuingInstitution,
          otherIssuingInstitution: {
            ...input.otherIssuingInstitution
          },
          sendDestination: input.sendDestination
        },
        applicationReview: {
          privateCaseDetails: {
            alternateVerifyingEntity: {
              isVerifiedWithOther: input.isVerifiedWithOther,
              otherDetails: input.otherDetails
            },
            verificationStatus: {
              verificationResponse: input.verificationResponse
            }
          }
        }
      }
    };

    const isOther = input?.issuingInstitution === null;

    if (!isOther) {
      inputData = {
        ...inputData,
        caseDetails: {
          ...inputData.caseDetails,
          application: {
            ...inputData?.caseDetails?.application,
            otherIssuingInstitution: data?.credentialVerificationCaseV1?.state !== 'decisionRendered' ? null : undefined
          }
        }
      };
    }

    if (input?.archiveEligibleAt) {
      inputData.archiveEligibleAt = input?.archiveEligibleAt;
    }
    if (input?.purgeEligibleAt && data?.credentialVerificationCaseV1?.state === 'draft') {
      inputData.purgeEligibleAt = input?.purgeEligibleAt;
    }
    if (input?.expirationEligibleAt) {
      inputData.expirationEligibleAt = input?.expirationEligibleAt || null;
    }

    return updateCredentialVerificationCase({
      variables: {
        input: inputData
      }
    });
  };

  const handleOnUploadSuccess = async (response: AzureBlobResponse, fieldName: string) => {
    const input = {
      id: props.caseId,
      caseDetails: {
        application: {
          credentialAssets: {
            [fieldName]: response.blobUrl,
            [`${fieldName}Version`]: response.versionId
          }
        }
      }
    };
    await updateCredentialVerificationCase({
      variables: {
        input
      }
    }).then(() => {
      message.success('File uploaded successfully');
    });
  };

  const handleOnUploadSuccessEntityProvidedCredentials = async (response: AzureBlobResponse, fieldName: string) => {
    const input = {
      id: props.caseId,
      caseDetails: {
        applicationReview: {
          privateCaseDetails: {
            entitySuppliedCredential: {
              [fieldName]: response.blobUrl,
              [`${fieldName}Version`]: response.versionId
            }
          }
        }
      }
    };

    await updateCredentialVerificationCase({
      variables: {
        input
      }
    }).then(() => {
      message.success('File uploaded successfully');
    });
  };

  return (
    <ComponentQueryLoader
      error={error || countryError || entitiesError || staffError}
      loading={loading || countryLoading || entitiesLoading || staffLoading}
      hasData={data !== undefined && staffData !== undefined}
      hasDataComponent={
        <Application
          staffUserType={staffData?.staffUserCurrent?.role?.roleSubtype as string}
          purgeEligibleAt={data?.credentialVerificationCaseV1?.purgeEligibleAt}
          archiveEligibleAt={data?.credentialVerificationCaseV1?.archiveEligibleAt}
          expirationEligibleAt={data?.credentialVerificationCaseV1?.expirationEligibleAt}
          caseId={data?.credentialVerificationCaseV1?.id}
          applicationReview={data?.credentialVerificationCaseV1?.caseDetails?.applicationReview as CredentialVerificationCaseV1ApplicationReview}
          application={data?.credentialVerificationCaseV1?.caseDetails?.application as CredentialVerificationCaseV1Application}
          caseName={data?.credentialVerificationCaseV1?.caseName as string}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          onSave={onSave}
          entities={entities?.entities as Entity[]}
          handleOnUploadSuccess={handleOnUploadSuccess}
          handleOnUploadSuccessEntityProvidedCredentials={handleOnUploadSuccessEntityProvidedCredentials}
          isCurrentAssigned={data?.credentialVerificationCaseV1?.isCurrentUserAssigned ?? false}
          caseState={data?.credentialVerificationCaseV1?.state ?? ''}
          isSavingApplication={isSavingApplication}
          isCaseOrApplicant={props.isCaseOrApplicant}
        />
      }
    />
  );
};
